import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCheckSquare, faSquare, faRobot, faTimes, faCheck, faSpinner
} from '@fortawesome/free-solid-svg-icons';
import { getSubtypeLabel, getCategoryLabel } from '../data/productTaxonomy';
import { MIXED_FUELS_SUBTYPE, getMixedFuelsLabel } from '../data/mixedFuelsSpec';

const ContractExtractionResults = ({ 
    extractedData, 
    selectedFields, 
    onFieldToggle, 
    onApply, 
    onDiscard, 
    onReAnalyze,
    reAnalysisGuidance,
    setReAnalysisGuidance,
    isReAnalyzing 
}) => {

    // Currency mappings for display with symbols
    const currencyLabels = {
        'USD': '$ USD - US Dollar',
        'EUR': '€ EUR - Euro',
        'GBP': '£ GBP - British Pound',
        'JPY': '¥ JPY - Japanese Yen',
        'CHF': 'CHF - Swiss Franc',
        'CAD': '$ CAD - Canadian Dollar',
        'AUD': '$ AUD - Australian Dollar',
        'CNY': '¥ CNY - Chinese Yuan',
        'HKD': '$ HKD - Hong Kong Dollar',
        'SGD': '$ SGD - Singapore Dollar',
        'BGN': 'BGN - Bulgarian Lev',
        'BRL': 'R$ BRL - Brazilian Real',
        'CZK': 'CZK - Czech Koruna',
        'DKK': 'DKK - Danish Krone',
        'HUF': 'HUF - Hungarian Forint',
        'IDR': 'IDR - Indonesian Rupiah',
        'ILS': '₪ ILS - Israeli Sheqel',
        'INR': '₹ INR - Indian Rupee',
        'ISK': 'ISK - Icelandic Króna',
        'KRW': '₩ KRW - South Korean Won',
        'MXN': '$ MXN - Mexican Peso',
        'MYR': 'MYR - Malaysian Ringgit',
        'NOK': 'NOK - Norwegian Krone',
        'NZD': '$ NZD - New Zealand Dollar',
        'PHP': '₱ PHP - Philippine Peso',
        'PLN': 'PLN - Polish Złoty',
        'RON': 'RON - Romanian Leu',
        'SEK': 'SEK - Swedish Krona',
        'THB': '฿ THB - Thai Baht',
        'TRY': '₺ TRY - Turkish Lira',
        'ZAR': 'R ZAR - South African Rand'
    };

    // Helper function to format field values for display
    const formatFieldValue = (field, value) => {
        if (!value) return value;

        switch (field) {
            case 'product_type':
                return getSubtypeLabel(value) || value;
            case 'category':
                return getCategoryLabel(value) || value;
            case 'source_location':
                return value; // Free text field, display as-is
            case 'price_currency':
                return currencyLabels[value] || value;
            case 'country_of_origin':
                return value; // Country names are already in proper format
            case 'mixed_fuels_fuel_category':
                return getMixedFuelsLabel('fuel_category', value);
            case 'mixed_fuels_iso_grade':
                return getMixedFuelsLabel('iso_grade', value);
            case 'mixed_fuels_sulfur_limit':
                return getMixedFuelsLabel('sulfur_limit', value);
            case 'mixed_fuels_iso_year':
                return getMixedFuelsLabel('iso_year', value);
            default:
                return value;
        }
    };

    // Field configuration for display. `showWhen` is optional - when present
    // the field card is omitted entirely if it returns false against the
    // current extraction. Used to keep the Other-category subtype label out
    // of the way for known subtypes, and to hide the Mixed Fuels section
    // for non-fuel products.
    const isOtherCategory = (data) =>
        data?.category === 'other' || data?.product_type === 'other';
    const isMixedFuels = (data) => data?.product_type === MIXED_FUELS_SUBTYPE;

    const stockItemFields = [
        { key: 'product_type', label: 'Product Type' },
        { key: 'subtype_label', label: 'Subtype Label', showWhen: isOtherCategory },
        { key: 'country_of_origin', label: 'Country of Origin' },
        { key: 'supplier', label: 'Supplier' },
        { key: 'source_location', label: 'Source Location' }
    ];

    const variantFields = [
        { key: 'shortname', label: 'Shortname' },
        { key: 'quality_specifications', label: 'Quality Specifications' },
        { key: 'quantity', label: 'Quantity' },
        { key: 'unit', label: 'Unit' },
        { key: 'size_specifications', label: 'Size Specifications' },
        { key: 'tolerance_lower_value', label: 'Tolerance Lower Value' },
        { key: 'tolerance_lower_unit', label: 'Tolerance Lower Unit' },
        { key: 'tolerance_upper_value', label: 'Tolerance Upper Value' },
        { key: 'tolerance_upper_unit', label: 'Tolerance Upper Unit' },
        { key: 'price_currency', label: 'Price Currency' },
        { key: 'purchase_price', label: 'Purchase Price' },
        { key: 'sale_price', label: 'Sale Price' }
    ];

    // Conditional spec fields shown only when the extracted product_type
    // is mixed_fuels. Driven by the same source list as the variant form so
    // labels stay consistent.
    const mixedFuelsSpecFields = [
        { key: 'mixed_fuels_fuel_category', label: 'Fuel Category' },
        { key: 'mixed_fuels_iso_grade', label: 'ISO Grade' },
        { key: 'mixed_fuels_sulfur_limit', label: 'Sulfur Limit' },
        { key: 'mixed_fuels_iso_year', label: 'ISO 8217 Year' },
    ];

    const visibleStockItemFields = stockItemFields.filter(
        (f) => !f.showWhen || f.showWhen(extractedData)
    );

    const FieldCard = ({ field, value, selected, onToggle }) => {
        const displayValue = formatFieldValue(field.key, value);
        const hasValue = value && value !== null && value !== '';
        
        return (
            <div 
                className={`extraction-field-card ${selected ? 'selected' : ''} ${hasValue ? 'has-value' : 'no-value'} ${!hasValue ? 'disabled' : ''}`} 
                onClick={hasValue ? onToggle : undefined}
                style={{ 
                    cursor: hasValue ? 'pointer' : 'not-allowed',
                    opacity: hasValue ? 1 : 0.6 
                }}
            >
                <div className="field-checkbox">
                    <FontAwesomeIcon 
                        icon={selected && hasValue ? faCheckSquare : faSquare} 
                        className={selected && hasValue ? 'text-blue-600' : 'text-gray-400'}
                    />
                </div>
                <div className="field-content">
                    <div className="field-label">{field.label}</div>
                    <div className="field-value">
                        {displayValue ? (
                            <span className="value-text">{displayValue}</span>
                        ) : (
                            <span className="no-value-text">Not found</span>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const hasSelectedFields = Object.values(selectedFields).some(Boolean);

    return (
        <div className="modal-body">
            {/* Header Section */}
            <div className="extraction-header">
                <div className="extraction-success-icon">
                    <FontAwesomeIcon icon={faRobot} style={{ color: '#3b82f6', fontSize: '1.5rem' }} />
                </div>
                <div className="extraction-message">
                    <h3 style={{ margin: '0 0 0.5rem 0', color: '#1e293b', fontSize: '1.25rem', fontWeight: 600 }}>
                        Contract Analysis Complete
                    </h3>
                    <p style={{ margin: 0, color: '#64748b', fontSize: '0.875rem' }}>
                        Review the extracted information and select which fields to use for creating your product item.
                    </p>
                </div>
            </div>
            
            {/* Product Item Section */}
            <div className="extraction-section">
                <div className="section-header">
                    <h4 style={{ margin: '0 0 0.5rem 0', color: '#374151', fontSize: '1rem', fontWeight: 600 }}>
                        Product Item Information
                    </h4>
                    <p style={{ margin: '0 0 1rem 0', color: '#6b7280', fontSize: '0.75rem' }}>
                        Basic product details that will be used to create the product item
                    </p>
                </div>
                <div className="extraction-fields-grid">
                    {visibleStockItemFields.map(field => (
                        <FieldCard 
                            key={field.key}
                            field={field}
                            value={extractedData[field.key]}
                            selected={selectedFields[field.key]}
                            onToggle={() => onFieldToggle(field.key)}
                        />
                    ))}
                </div>
            </div>
            
            {/* Variant Section */}
            <div className="extraction-section">
                <div className="section-header">
                    <h4 style={{ margin: '0 0 0.5rem 0', color: '#374151', fontSize: '1rem', fontWeight: 600 }}>
                        Variant Information
                    </h4>
                    <p style={{ margin: '0 0 1rem 0', color: '#6b7280', fontSize: '0.75rem' }}>
                        Specific details that will be used to create the stock variant
                    </p>
                </div>
                <div className="extraction-fields-grid">
                    {variantFields.map(field => (
                        <FieldCard 
                            key={field.key}
                            field={field}
                            value={extractedData[field.key]}
                            selected={selectedFields[field.key]}
                            onToggle={() => onFieldToggle(field.key)}
                        />
                    ))}
                </div>
            </div>

            {/* Mixed Fuels Spec Section - only shown when the extracted
                product_type is mixed_fuels, driven by the same taxonomy as
                the variant form's conditional spec block. */}
            {isMixedFuels(extractedData) && (
                <div className="extraction-section">
                    <div className="section-header">
                        <h4 style={{ margin: '0 0 0.5rem 0', color: '#374151', fontSize: '1rem', fontWeight: 600 }}>
                            Mixed Fuels Spec
                        </h4>
                        <p style={{ margin: '0 0 1rem 0', color: '#6b7280', fontSize: '0.75rem' }}>
                            Structured ISO 8217 spec attributes detected for this fuel contract. Selected values pre-fill the variant spec dropdowns.
                        </p>
                    </div>
                    <div className="extraction-fields-grid">
                        {mixedFuelsSpecFields.map(field => (
                            <FieldCard
                                key={field.key}
                                field={field}
                                value={extractedData[field.key]}
                                selected={selectedFields[field.key]}
                                onToggle={() => onFieldToggle(field.key)}
                            />
                        ))}
                    </div>
                </div>
            )}
            
            {/* Re-analysis Section */}
            <div className="form-group" style={{ marginTop: '2rem' }}>
                <label className="form-label" style={{ fontSize: '0.875rem', fontWeight: 600, color: '#374151', marginBottom: '0.5rem', display: 'block' }}>
                    Need better results? Provide specific guidance for re-analysis:
                </label>
                <textarea
                    className="form-textarea"
                    placeholder="e.g., 'quantity is 44MT', 'supplier is ABC Trading Corp', 'price is $150 per metric ton', 'product type is steam coal'"
                    value={reAnalysisGuidance}
                    onChange={(e) => setReAnalysisGuidance(e.target.value)}
                    disabled={isReAnalyzing}
                    rows={3}
                    style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #e2e8f0',
                        borderRadius: '0.375rem',
                        fontSize: '0.875rem',
                        lineHeight: '1.5',
                        color: '#374151',
                        backgroundColor: isReAnalyzing ? '#f9fafb' : 'white',
                        resize: 'vertical',
                        minHeight: '80px',
                        fontFamily: 'inherit',
                        outline: 'none',
                        transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
                        marginBottom: '1rem'
                    }}
                    onFocus={(e) => {
                        e.target.style.borderColor = '#3b82f6';
                        e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                    }}
                    onBlur={(e) => {
                        e.target.style.borderColor = '#e2e8f0';
                        e.target.style.boxShadow = 'none';
                    }}
                />
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button 
                        onClick={() => onReAnalyze(reAnalysisGuidance)}
                        disabled={isReAnalyzing || !reAnalysisGuidance.trim()}
                        className="btn btn-secondary"
                        type="button"
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            padding: '0.75rem 1rem',
                            backgroundColor: '#f8fafc',
                            color: '#475569',
                            border: '1px solid #e2e8f0',
                            borderRadius: '0.375rem',
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            cursor: isReAnalyzing || !reAnalysisGuidance.trim() ? 'not-allowed' : 'pointer',
                            opacity: isReAnalyzing || !reAnalysisGuidance.trim() ? 0.6 : 1,
                            transition: 'all 0.2s ease',
                            whiteSpace: 'nowrap'
                        }}
                        onMouseEnter={(e) => {
                            if (!isReAnalyzing && reAnalysisGuidance.trim()) {
                                e.target.style.backgroundColor = '#f1f5f9';
                                e.target.style.borderColor = '#cbd5e1';
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (!isReAnalyzing && reAnalysisGuidance.trim()) {
                                e.target.style.backgroundColor = '#f8fafc';
                                e.target.style.borderColor = '#e2e8f0';
                            }
                        }}
                    >
                        {isReAnalyzing ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} spin />
                                Re-analyzing...
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faRobot} style={{ color: '#3b82f6' }} />
                                Re-analyze
                            </>
                        )}
                    </button>
                </div>
            </div>
            
            {/* Action Buttons */}
            <div className="modal-actions centered" style={{ 
                marginTop: '2rem', 
                borderTop: '1px solid #f1f5f9',
                display: 'flex',
                gap: '0.75rem',
                justifyContent: 'center'
            }}>
                <button 
                    onClick={onDiscard}
                    className="btn btn-secondary"
                    disabled={isReAnalyzing}
                    type="button"
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        minWidth: '120px',
                        justifyContent: 'center',
                        padding: '0.75rem 1rem',
                        backgroundColor: '#ffffff',
                        color: '#475569',
                        border: '1px solid #e2e8f0',
                        borderRadius: '0.375rem',
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        cursor: isReAnalyzing ? 'not-allowed' : 'pointer',
                        opacity: isReAnalyzing ? 0.6 : 1,
                        transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                        if (!isReAnalyzing) {
                            e.target.style.backgroundColor = '#f8fafc';
                            e.target.style.borderColor = '#cbd5e1';
                            e.target.style.transform = 'translateY(-1px)';
                        }
                    }}
                    onMouseLeave={(e) => {
                        if (!isReAnalyzing) {
                            e.target.style.backgroundColor = '#ffffff';
                            e.target.style.borderColor = '#e2e8f0';
                            e.target.style.transform = 'translateY(0)';
                        }
                    }}
                >
                    <FontAwesomeIcon icon={faTimes} style={{ fontSize: '0.875rem' }} />
                    Discard
                </button>
                
                <button 
                    onClick={onApply}
                    className="btn btn-primary"
                    disabled={isReAnalyzing}
                    type="button"
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        minWidth: '120px',
                        justifyContent: 'center',
                        padding: '0.75rem 1rem',
                        background: !isReAnalyzing ? 
                            'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)' : '#94a3b8',
                        color: 'white',
                        border: 'none',
                        borderRadius: '0.375rem',
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        cursor: isReAnalyzing ? 'not-allowed' : 'pointer',
                        opacity: isReAnalyzing ? 0.6 : 1,
                        transition: 'all 0.2s ease',
                        boxShadow: !isReAnalyzing ? 
                            '0 4px 12px rgba(59, 130, 246, 0.4)' : 'none'
                    }}
                    onMouseEnter={(e) => {
                        if (!isReAnalyzing) {
                            e.target.style.background = 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)';
                            e.target.style.transform = 'translateY(-1px)';
                            e.target.style.boxShadow = '0 6px 16px rgba(59, 130, 246, 0.5)';
                        }
                    }}
                    onMouseLeave={(e) => {
                        if (!isReAnalyzing) {
                            e.target.style.background = 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)';
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.4)';
                        }
                    }}
                >
                    <FontAwesomeIcon icon={faCheck} style={{ fontSize: '0.875rem' }} />
                    {hasSelectedFields ? 
                        `Apply Selected (${Object.values(selectedFields).filter(Boolean).length})` : 
                        'Proceed without Data'
                    }
                </button>
            </div>
        </div>
    );
};

export default ContractExtractionResults; 